import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import classNames from "classnames";
import Button from "components/Common/Button";
import Checkbox from "components/Common/Checkbox";
import Select from "components/Common/Select";
import ButtonGroupMultiSelect from "components/Common/ButtonGroupMultiSelect";
import DatePickerDropdown from "components/Common/DatePickerDropdown/";
import colors from "stylesheets/utilities/colors.scss";
import { scheduleRuleRow } from "../../reducer";
import "./style.scss";

const BLOCK_NAME = "SettingsScheduleRow";
const DAY_SELECTOR_OPTIONS = [
  { label: "Su", value: "Sunday", altText: "Sunday" },
  { label: "Mo", value: "Monday", altText: "Monday" },
  { label: "Tu", value: "Tuesday", altText: "Tuesday" },
  { label: "We", value: "Wednesday", altText: "Wednesday" },
  { label: "Th", value: "Thursday", altText: "Thursday" },
  { label: "Fr", value: "Friday", altText: "Friday" },
  { label: "Sa", value: "Saturday", altText: "Saturday" }
];
const LABEL_TIME_FORMAT = "h:mma";
const VALUE_TIME_FORMAT = "HH:mm";
const DEFAULT_START_TIME = "00:00";
const DEFAULT_END_TIME = "23:59";

class SettingsScheduleRow extends React.Component {
  /**
   * @param {Object} startTime - moment time
   * @param {Object} endTime - moment time
   * @param {Number} interval - interval of minute increment
   * @returns {Object} - selector options
   */
  static generateTimeRange(startTime, endTime, interval = 30) {
    const timeRanges = [];

    while (startTime.isBefore(endTime) || startTime.isSame(endTime)) {
      timeRanges.push({
        label: startTime.format(LABEL_TIME_FORMAT),
        value: startTime.format(VALUE_TIME_FORMAT)
      });
      startTime.add(interval, "minutes");
    }

    // Add final interval
    timeRanges.push({
      label: "12:00am",
      value: DEFAULT_END_TIME
    });

    return timeRanges;
  }

  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.handleDaysUpdate = this.handleDaysUpdate.bind(this);
    this.handleStartTimeUpdate = this.handleStartTimeUpdate.bind(this);
    this.handleEndTimeUpdate = this.handleEndTimeUpdate.bind(this);
    this.handleAllDayUpdate = this.handleAllDayUpdate.bind(this);
    this.handleExceptionDateUpdate = this.handleExceptionDateUpdate.bind(this);
  }

  /**
   *
   * @param {String} field
   * @returns {String}
   */
  getFieldErrorMessage(field) {
    if (!this.isFieldInvalid(field)) {
      return "";
    }

    const { row } = this.props;
    const rowInvalidFields = row.get("invalidFields");
    const invalidField = rowInvalidFields.find(invalidFields => invalidFields &&
      invalidFields.get("field") === field);

    return invalidField && invalidField.get("errorMessage");
  }

  /**
   * Helper function to determine if a particular field is found to be invalid
   * @param {String} field
   * @returns {Boolean}
   */
  isFieldInvalid(field) {
    const { row } = this.props;
    const rowInvalidFields = row.get("invalidFields");

    return rowInvalidFields && rowInvalidFields.findIndex(invalidFields => invalidFields &&
      invalidFields.get("field") === field) > -1;
  }

  /**
   * @param {Array} value
   */
  handleDaysUpdate(value) {
    this.handleRowUpdate({ days: value });
  }

  /**
   * @param {String} value
   */
  handleStartTimeUpdate(value) {
    this.handleRowUpdate({ start: value });
  }

  /**
   * @param {String} value
   */
  handleEndTimeUpdate(value) {
    this.handleRowUpdate({ end: value });
  }

  /**
   * @param {String} value
   */
  handleAllDayUpdate(value) {
    this.handleRowUpdate({ allDay: value });
  }

  /**
   * @param {String} value
   */
  handleExceptionDateUpdate(value) {
    this.handleRowUpdate({ date: value });
  }

  /**
   * @param {Object} value
   */
  handleRowUpdate(value) {
    const { rowIndex, onChange } = this.props;

    onChange({ index: rowIndex, value });
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      rowIndex,
      row,
      days,
      start,
      end,
      allDay,
      exceptionDate,
      hasExpectionDatePicker,
      onDelete
    } = this.props;

    const fromTimeSelectOptions = SettingsScheduleRow.generateTimeRange(
      moment(DEFAULT_START_TIME, VALUE_TIME_FORMAT),
      moment(DEFAULT_END_TIME, VALUE_TIME_FORMAT)
    );

    // add 30 minutes to prevent overlapping time with the from-times
    const toTimeSelectOptions = SettingsScheduleRow.generateTimeRange(
      moment(DEFAULT_START_TIME, VALUE_TIME_FORMAT),
      moment(DEFAULT_END_TIME, VALUE_TIME_FORMAT)
    );

    const rowConflictingDays = row.getIn(["conflictingDays", "days"]);
    const rowConflictingDaysErrorMessage = row.getIn(["conflictingDays", "errorMessage"]);

    return (
      <div className={`${BLOCK_NAME}__row`}>
        {hasExpectionDatePicker ? (
          <div className={`${BLOCK_NAME}__date-selector`}>
            <DatePickerDropdown
              activeInput="start"
              onChange={this.handleExceptionDateUpdate}
              selectedDate={exceptionDate}
            />
          </div>
        ) : (
          <div className={`${BLOCK_NAME}__day-selector`}>
            <ButtonGroupMultiSelect
              isInvalid={this.isFieldInvalid("days")}
              customClassName={`${BLOCK_NAME}__day-selector-buttons`}
              options={DAY_SELECTOR_OPTIONS}
              values={days}
              invalidFields={rowConflictingDays.toJS()}
              onChange={this.handleDaysUpdate}
            />
            {
              Boolean(this.getFieldErrorMessage("days")) &&
              <p className={`${BLOCK_NAME}__row__error`}>{this.getFieldErrorMessage("days")}</p>
            }
            {
              Boolean(rowConflictingDays.size) &&
              <p className={`${BLOCK_NAME}__row__error`}>{rowConflictingDaysErrorMessage}</p>
            }
          </div>
        )}

        <div>
          <div className={`${BLOCK_NAME}__time-selector-container`}>
            <Select
              isInvalid={this.isFieldInvalid("startEnd")}
              disabled={allDay}
              customClassName={classNames(
                `${BLOCK_NAME}__time-selector`,
                `${BLOCK_NAME}__time-selector--from`
              )}
              value={allDay ? "—" : start}
              options={fromTimeSelectOptions}
              placeholder={allDay ? "—" : fromTimeSelectOptions[0].label}
              showIconBesideSelected
              onChange={this.handleStartTimeUpdate}
            />
            <div className={classNames(
              `${BLOCK_NAME}__label`,
              { [`${BLOCK_NAME}__label__all-day`]: allDay }
            )}
            >to
            </div>
            <Select
              isInvalid={this.isFieldInvalid("startEnd")}
              disabled={allDay}
              customClassName={`${BLOCK_NAME}__time-selector ${BLOCK_NAME}__time-selector--to`}
              value={allDay ? "—" : end}
              options={toTimeSelectOptions}
              placeholder="—"
              onChange={this.handleEndTimeUpdate}
            />
          </div>
          {
            Boolean(this.getFieldErrorMessage("startEnd")) &&
            <p className={`${BLOCK_NAME}__row__error`}>{this.getFieldErrorMessage("startEnd")}</p>
          }
        </div>

        <div className={`${BLOCK_NAME}__all-day`}>
          <div className={`${BLOCK_NAME}__all-day__checkbox`}>
            <Checkbox
              checked={allDay}
              handleToggle={this.handleAllDayUpdate}
            />
          </div>
          <div className={`${BLOCK_NAME}__all-day__label`}>All Day</div>
        </div>

        <Button
          customClassName={`${BLOCK_NAME}__remove-row`}
          onClick={() => onDelete({ index: rowIndex })}
          icon="CircleRemoveAlternate"
          fillColor={colors.colorUIBad}
          noMargin
          clear
        />
      </div>
    );
  }
}

SettingsScheduleRow.propTypes = {
  row: PropTypes.instanceOf(scheduleRuleRow).isRequired,
  rowIndex: PropTypes.number.isRequired,
  days: PropTypes.arrayOf(PropTypes.string),
  start: PropTypes.string,
  end: PropTypes.string,
  allDay: PropTypes.bool,
  exceptionDate: PropTypes.string,
  hasExpectionDatePicker: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

SettingsScheduleRow.defaultProps = {
  days: [],
  start: null,
  end: null,
  allDay: false,
  exceptionDate: null,
  hasExpectionDatePicker: false
};

export default SettingsScheduleRow;
